/**
 * The lighting master (2026-10-02). PURE - no signals, no storage, no
 * network - so the rules can be tested on their own
 * (`smokes/timeline_light_master_rules.ts`) and so OPBridge, which follows
 * the OPButtons timeline once it is done, can port exactly this.
 *
 * Operator: "the lighting rig should have a master group track which
 * overrides any individual automation. if i put a group marker on bar 4 it'd
 * be good to be able to put a transition marker before it so that the
 * individual lights can all transition to the global setting." And, before
 * that: "if it's a light lane then mute would mean off".
 *
 * THE MODEL
 *   - An "All lights" lane (`laneKind: 'dmx-rig'`) is a MASTER. Its colour
 *     keyframes do not play all the time: a keyframe STARTS a takeover, and
 *     the takeover runs through the keyframes after it until a "Hand back"
 *     (`lightRelease`) or the end of the timeline. Inside a takeover every
 *     light shows the master's colour, whatever its own lane says.
 *   - The keyframe that starts a takeover may have a lead-in
 *     (`leadInBars`): over those bars every light blends from what it shows
 *     into the master's first colour, ARRIVING on the keyframe's beat. A Hand
 *     back may have one too: the lights blend back to their own lanes,
 *     arriving on the Hand back's beat. No lead-in is a cut on the beat.
 *   - Between keyframes inside a takeover the master's own Cut / Fade apply
 *     (`colorSample.ts`), as on any lights lane.
 *   - A light lane (`dmx-fixture`) plays its own keyframes, leaving the light
 *     alone before its first. Several masters: the higher lane wins.
 *   - Mute: a muted (or un-soloed) LIGHT is OFF (black), except inside a
 *     takeover, where the master still drives it. A muted MASTER makes no
 *     takeovers: the lights play their own lanes.
 *   - A Hand back and a keyframe on the same beat: the Hand back ends the
 *     takeover first, the keyframe starts a new one.
 *   - LIGHT GROUPS (2026-10-03, DEC-251): "i need to be able to separate
 *     individual lights into another group so i can have all but 1 on a
 *     master control". Every lights lane has a group (`lightGroup`, absent
 *     for Lighting itself). A master takes over ONLY the lights in its own
 *     group (`masterReaches`): the groups are separate, nothing reaches
 *     across them. With no groups this is every light, as before.
 *   - THE ALL-LIGHTS MASTER (DEC-329, OPBridge S318's shape, DEC-686): a master
 *     with `allLights` reaches every light and ranks above every group master
 *     wherever its lane sits (`mastersReaching`); between two, the upper lane
 *     wins. A light kept out of masters on OPBridge is reached by none.
 */

import type { ColorMarkerStep, LightReleaseStep, TimelineLane, ValuePointCurve } from '@opbuttons/schema';
import { isColorMarkerStep, isLightReleaseStep, unrollPatterns } from '@opbuttons/schema';
import { applyCurve, sampleColorSegment, type Rgb01 } from './colorSample.ts';

export const OFF: Rgb01 = { r: 0, g: 0, b: 0 };

/** One stretch where a master drives every light. */
export interface Takeover {
  /** The keyframe that starts it. */
  start: number;
  /** Where it ends: its Hand back, or the end of the timeline. */
  end: number;
  /** The fade in, ending on `start` (clamped to the gap before it). */
  leadIn: number;
  leadInCurve: ValuePointCurve;
  /** The fade back, ending on `end` (clamped inside the takeover). */
  release: number;
  releaseCurve: ValuePointCurve;
  /** The master's keyframes inside it, in time order (the first starts it). */
  keys: ColorMarkerStep[];
  /** The Hand back that ends it, if any. */
  handBack: LightReleaseStep | null;
}

/** The light group a lane sits in; `''` is Lighting itself. A blank or
 *  whitespace name is Lighting too, so a stray value never makes a group. */
export function lightGroupOf(lane: TimelineLane): string {
  return (lane.lightGroup ?? '').trim();
}

/** Whether `master` takes over `light`: an All-lights master always, any other only inside its own light group. */
export function masterReaches(master: TimelineLane, light: TimelineLane): boolean {
  return master.allLights === true || lightGroupOf(master) === lightGroupOf(light);
}

/** The masters that take over `light`, in the order `lightColourAt` wants them, highest first: every All-lights master,
 *  then the group masters, each in lane order (OPBridge S318, number for number). `keptOut` (OPBridge's
 *  `keepOutOfMasters`) means none reach it. */
export function mastersReaching<M extends TimelineLane>(masters: readonly M[], light: TimelineLane, keptOut = false): M[] {
  if (keptOut) return [];
  const reach = masters.filter((m) => masterReaches(m, light));
  return [...reach.filter((m) => m.allLights === true), ...reach.filter((m) => m.allLights !== true)];
}

/** The lane that drives ZONE `zone` of light `fixtureId` (OPBridge S329, `timeline_light_zones`, whose rule this
 *  mirrors as they asked): the lane naming that zone wins it whatever the lane order; else the light's lane with no
 *  zone fans to it. Null when no lane drives it. */
export function zoneLaneFor<L extends Pick<TimelineLane, 'fixtureRef'>>(lanes: readonly L[], fixtureId: string, zone: number): L | null {
  const mine = lanes.filter((l) => l.fixtureRef?.fixtureId === fixtureId);
  return mine.find((l) => l.fixtureRef?.subfixture === zone) ?? mine.find((l) => l.fixtureRef?.subfixture === undefined) ?? null;
}

/** A master lane's takeovers, in time order. */
export function takeoversOf(lane: TimelineLane, totalBars: number): Takeover[] {
  // A pattern block plays as its keys written out (2026-10-20), on a master too.
  const points = unrollPatterns(lane.steps)
    .filter((s): s is ColorMarkerStep | LightReleaseStep => isColorMarkerStep(s) || isLightReleaseStep(s))
    // Time order; on one beat the Hand back first (it ends, then a keyframe starts).
    .sort((a, b) => a.position - b.position || (a.kind === 'lightRelease' ? -1 : 1) - (b.kind === 'lightRelease' ? -1 : 1));
  const out: Takeover[] = [];
  let cur: Takeover | null = null;
  let prevEnd = 0;
  for (const p of points) {
    if (p.kind === 'colorMarker') {
      if (cur) { cur.keys.push(p); continue; }
      const room = Math.max(0, p.position - prevEnd);
      cur = {
        start: p.position, end: totalBars, keys: [p], handBack: null,
        leadIn: Math.min(room, Math.max(0, p.leadInBars ?? 0)), leadInCurve: p.leadInCurve ?? 'linear',
        release: 0, releaseCurve: 'linear',
      };
      continue;
    }
    if (!cur) continue; // a Hand back with nothing to hand back
    cur.end = p.position;
    cur.handBack = p;
    cur.release = Math.min(Math.max(0, cur.end - cur.start), Math.max(0, p.leadInBars ?? 0));
    cur.releaseCurve = p.leadInCurve ?? 'linear';
    out.push(cur);
    prevEnd = cur.end;
    cur = null;
  }
  if (cur) out.push(cur);
  return out;
}

/** How much the master drives the lights at `pos`, 0..1: 0 outside, 1
 *  inside, and the shaped ramp through a lead-in or a fade back. */
export function takeoverWeight(tk: Takeover, pos: number): number {
  if (pos >= tk.end - 1e-9 || pos < tk.start - tk.leadIn - 1e-9) return 0;
  if (pos < tk.start) return tk.leadIn > 0 ? applyCurve((pos - (tk.start - tk.leadIn)) / tk.leadIn, tk.leadInCurve) : 0;
  if (tk.release > 0 && pos >= tk.end - tk.release) return 1 - applyCurve((pos - (tk.end - tk.release)) / tk.release, tk.releaseCurve);
  return 1;
}

/** The master's colour at `pos` in a takeover (its first colour through the
 *  lead-in, its last to the end). */
export function takeoverColour(tk: Takeover, pos: number): Rgb01 {
  return colourAlong(tk.keys, pos);
}

/** The colour along time-ordered keys at `pos`: the cut or fade from the last key at or before it towards the next,
 *  the first key's colour before them all and the last's after. A binary search (2026-11-02): the colour stream
 *  samples lanes of thousands of keys thousands of times, where a scan per sample was quadratic. */
function colourAlong(k: readonly ColorMarkerStep[], pos: number): Rgb01 {
  let lo = 0; let hi = k.length - 1; let i = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (k[mid]!.position <= pos) { i = mid; lo = mid + 1; } else hi = mid - 1;
  }
  if (i < 0) return { ...k[0]!.color };
  if (i >= k.length - 1) return { ...k[k.length - 1]!.color };
  const a = k[i]!; const b = k[i + 1]!;
  if (pos <= a.position) return { ...a.color };
  return sampleColorSegment(a, b, (pos - a.position) / (b.position - a.position));
}

/** The id of the dark keyframe a dark start puts on bar 0 of a light's lane (`darkStartSteps`). */
export const darkStartKeyId = (laneId: string): string => `${laneId}~dark`;

/** The steps of a light's lane for a timeline that starts dark (`timelineStartsDark`, 2026-10-20): unless a colour
 *  keyframe already sits on bar 0, an OFF keyframe held there (`hold`, so it stays black and cuts to the first colour
 *  rather than fading up to it). `steps` are as they play (patterns written out, the lead-in applied). A lane with no
 *  colour at all becomes black for the run, which is what a light whose lane says nothing should be in a dark start. */
export function darkStartSteps<S extends { position: number; kind: string }>(laneId: string, steps: readonly S[]): Array<S | ColorMarkerStep> {
  if (steps.some((s) => s.kind === 'colorMarker' && s.position <= 1e-9)) return [...steps];
  const off: ColorMarkerStep = { id: darkStartKeyId(laneId), kind: 'colorMarker', position: 0, color: { ...OFF }, curve: 'hold' };
  return [off, ...steps];
}

/** A light lane's own colour at `pos`, or null before its first keyframe
 *  (the lane leaves the light alone there). */
export function ownColour(lane: TimelineLane, pos: number): Rgb01 | null {
  return ownColourOf(lane)(pos);
}

/** `ownColour` for one lane at many moments: its keys written out and sorted once. */
export function ownColourOf(lane: TimelineLane): (pos: number) => Rgb01 | null {
  const k = unrollPatterns(lane.steps).filter(isColorMarkerStep).sort((a, b) => a.position - b.position);
  return (pos) => (k.length === 0 || pos < k[0]!.position - 1e-9 ? null : colourAlong(k, pos));
}

function mix(a: Rgb01, b: Rgb01, t: number): Rgb01 {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/**
 * What ONE light shows at `pos`: its own lane (or off when that lane is
 * silent), with every audible master's takeover blended over it, the higher
 * master last so it wins. `masters` are in lane order, top first. Returns
 * null when nothing drives the light (left alone). Otherwise `colour`, and
 * `over`, how strongly a master drives it (0..1); `alone` says the light's
 * own lane says nothing there, so a master fading in fades from whatever the
 * light was showing, which only the rig knows (the colour is then the
 * master's, at strength `over`).
 */
export function lightColourAt(
  own: { lane: TimelineLane; audible: boolean } | null,
  masters: ReadonlyArray<{ takeovers: Takeover[]; audible: boolean }>,
  pos: number,
): { colour: Rgb01; over: number; alone: boolean } | null {
  return lightColourWith(own ? { at: (p) => ownColour(own.lane, p), audible: own.audible } : null, masters, pos);
}

/** `lightColourAt` with the light's own colour given as a function (`ownColourOf`), for sampling one light many times. */
export function lightColourWith(
  own: { at: (pos: number) => Rgb01 | null; audible: boolean } | null,
  masters: ReadonlyArray<{ takeovers: Takeover[]; audible: boolean }>,
  pos: number,
): { colour: Rgb01; over: number; alone: boolean } | null {
  let colour: Rgb01 | null = own ? (own.audible ? own.at(pos) : { ...OFF }) : null;
  const alone = colour === null;
  let over = 0;
  for (let i = masters.length - 1; i >= 0; i--) {
    const m = masters[i]!;
    if (!m.audible) continue;
    for (const tk of m.takeovers) {
      const w = takeoverWeight(tk, pos);
      if (w <= 0) continue;
      const mc = takeoverColour(tk, pos);
      colour = colour ? mix(colour, mc, w) : mc;
      over = Math.max(over, w);
    }
  }
  return colour ? { colour, over, alone } : null;
}
